/**
 *
 * Description:
 * This file defines two React functional components:
 * 1. `DatePickerInput` - A reusable date picker input component with configurable props including validation,
 *    min/max dates, clearable actions, and error handling. It uses `@mui/x-date-pickers` and Moment.js.
 * 2. `ScheduleDatePickerInput` - A lighter version of the date picker used specifically for scheduling
 *    purposes. Uses `onChange` instead of `onAccept`.
 *
 */
import React, { useCallback, useMemo, useState } from "react";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from "moment";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import './date-picker.scss'
import _ from 'lodash'

/**
 * Converts a moment or date value into a standardized UTC ISO string.
 * @param {import('moment').Moment|null|undefined} value - Date value to convert.
 * @returns {string|null|undefined} UTC ISO string, null, or undefined.
 */
const getIsoValue = (value) => {
    if (value === null) return null;
    return moment.isMoment(value) && value.isValid() ? moment(value).toISOString() : undefined;
};

/**
 * Standard reusable Date Picker input component wrapped with MUI LocalizationProvider and DesktopDatePicker.
 * @param {object} props - Component properties.
 * @param {string} [props.label] - Input field label.
 * @param {string} [props.placeholder] - Input placeholder fallback.
 * @param {string|Date|moment.Moment} [props.maxDate] - Maximum selectable date.
 * @param {string|Date|moment.Moment} [props.minDate] - Minimum selectable date.
 * @param {boolean} [props.disabled] - Whether the datepicker is disabled.
 * @param {boolean} [props.disable] - Legacy alias for disabled.
 * @param {string} [props.className] - Extra CSS classes.
 * @param {boolean} [props.required] - Whether field is required.
 * @param {string} [props.error] - Validation error message text.
 * @param {boolean} [props.clearable=true] - Whether clear button is enabled.
 * @param {string[]} [props.validation] - Validation rules array.
 * @param {string} [props.dataCy] - Cypress test identifier.
 * @param {function} [props.onChange] - Callback receiving the ISO string on commit.
 * @returns {React.ReactElement} The rendered DatePickerInput component.
 */
export default function DatePickerInput({ label, placeholder, maxDate, disabled, disable, className = '', required, error, clearable = true, validation = [], minDate, dataCy = '', ...props }) {
    let isRequired = required || (validation.includes('required') && !(validation.includes('skip') || validation.includes('optional')));
    const [open, setOpen] = useState(false);
    const isDisabled = disabled || disable;
    const pickerValue = useMemo(() => props.value ? moment.utc(props.value) : null, [props.value]);
    const minimumDate = useMemo(() => minDate ? moment(minDate) : null, [minDate]);
    const maximumDate = useMemo(() => maxDate ? moment(maxDate) : null, [maxDate]);
    const handleOpen = useCallback((e) => {
        if (isDisabled) return;
        if (e && e.target && e.target.closest && e.target.closest('.contractable-picker-popper')) return;
        setOpen(true);
    }, [isDisabled]);
    const handleClose = useCallback(() => setOpen(false), []);
    const handleAccept = useCallback((newValue) => {
        setOpen(false);
        const isoValue = getIsoValue(newValue);
        if (isoValue !== undefined && props.onChange) props.onChange(isoValue);
    }, [props.onChange]);

    return (
        <div className="form-group">
            <div className={_.uniq(["input-block", "datepicker-block", ...className.split(' ')]).join(' ')} data-cy={dataCy} onClick={handleOpen}>
                <span className="input-icon icon-grey"><CalendarMonthIcon /></span>
                <label className="input-label">
                    <span>{label || placeholder || ''} {isRequired ? <span className='text-danger'>*</span> : null}</span>
                </label>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DesktopDatePicker
                        id={props.name}
                        name={props.name}
                        data-cy={dataCy || props['data-cy'] || 'date-picker'}
                        margin='dense'
                        inputVariant='outlined'
                        value={pickerValue}
                        format="DD/MM/YYYY"
                        // onChange={(value) => props.onChange(moment(value).toISOString())}
                        onAccept={handleAccept}
                        maxDate={maximumDate}
                        disabled={isDisabled}
                        open={open}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        disableOpenPicker
                        closeOnSelect={false}
                        slotProps={{
                            actionBar: {
                                actions: [...(clearable ? ['clear'] : []), 'cancel', 'accept'],
                                onCancel: handleClose
                            },
                            popper: {
                                className: 'contractable-picker-popper contractable-date-picker-popper',
                                style: { zIndex: 10000 },
                                popperOptions: { strategy: 'fixed' },
                                onClick: (e) => e.stopPropagation()
                            },
                            desktopPaper: { className: 'contractable-picker-paper' },
                            textField: {
                                helperText: error ? <small className="float-start text-danger">{error}</small> : "",
                                error,
                                inputProps: { 'data-cy': props['data-cy'] || undefined }
                            }
                        }}
                        popperProps={props.popperProps}
                        minDate={minimumDate}
                    />
                </LocalizationProvider>
            </div>
        </div >
    );
}

/**
 * Specialized Date Picker input component tailored for scheduling workflows.
 * @param {object} props - Component properties.
 * @param {string} [props.label] - Input field label.
 * @param {string} [props.placeholder] - Input placeholder fallback.
 * @param {string|Date|moment.Moment} [props.maxDate] - Maximum selectable date.
 * @param {boolean} [props.disabled] - Whether the datepicker is disabled.
 * @param {boolean} [props.disable] - Legacy alias for disabled.
 * @param {string} [props.className] - Extra CSS classes.
 * @param {boolean} [props.required] - Whether field is required.
 * @param {string} [props.error] - Validation error message text.
 * @param {boolean} [props.clearable=true] - Whether clear button is enabled.
 * @param {string[]} [props.validation] - Validation rules array.
 * @param {string} [props.dataCy] - Cypress test identifier.
 * @param {function} [props.onChange] - Intermediate change callback.
 * @param {function} [props.onAccept] - Final commit callback on date acceptance.
 * @returns {React.ReactElement} The rendered ScheduleDatePickerInput component.
 */
export function ScheduleDatePickerInput({ label, placeholder, maxDate, disabled, disable, className = '', required, error, clearable = true, validation = [], dataCy = '', ...props }) {
    let isRequired = required || (validation.includes('required') && !(validation.includes('skip') || validation.includes('optional')));
    const [open, setOpen] = useState(false);
    const isDisabled = disabled || disable;
    const pickerValue = useMemo(() => props.value ? moment.utc(props.value) : null, [props.value]);
    const maximumDate = useMemo(() => maxDate ? moment(maxDate) : null, [maxDate]);
    const handleOpen = useCallback((e) => {
        if (isDisabled) return;
        if (e && e.target && e.target.closest && e.target.closest('.contractable-picker-popper')) return;
        setOpen(true);
    }, [isDisabled]);
    const handleClose = useCallback(() => setOpen(false), []);
    const handleChange = useCallback((newValue) => {
        const isoValue = getIsoValue(newValue);
        if (isoValue !== undefined && props.onChange) props.onChange(isoValue);
    }, [props.onChange]);
    const handleAccept = useCallback((newValue) => {
        setOpen(false);
        const isoValue = getIsoValue(newValue);
        if (isoValue !== undefined && props.onAccept) props.onAccept(isoValue);
    }, [props.onAccept]);

    return (
        <div className="form-group">
            <div className={_.uniq(["input-block", "datepicker-block", ...className.split(' ')]).join(' ')} data-cy={dataCy} onClick={handleOpen}>
                <span className="input-icon icon-grey"><CalendarMonthIcon /></span>
                <label className="input-label">
                    <span>{label || placeholder || ''} {isRequired ? <span className='text-danger'>*</span> : null}</span>
                </label>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DesktopDatePicker
                        id={props.name}
                        data-cy={dataCy || props['data-cy'] || 'date-picker'}
                        margin='dense'
                        inputVariant='outlined'
                        value={pickerValue}
                        format="DD/MM/YYYY"
                        onChange={handleChange}
                        onAccept={handleAccept}
                        maxDate={maximumDate}
                        disabled={isDisabled}
                        open={open}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        disableOpenPicker
                        closeOnSelect={false}
                        slotProps={{
                            actionBar: {
                                actions: [...(clearable ? ['clear'] : []), 'cancel', 'accept'],
                                onCancel: handleClose
                            },
                            popper: {
                                className: 'contractable-picker-popper contractable-date-picker-popper',
                                style: { zIndex: 10000 },
                                popperOptions: { strategy: 'fixed' },
                                onClick: (e) => e.stopPropagation()
                            },
                            desktopPaper: { className: 'contractable-picker-paper' },
                            textField: {
                                helperText: error ? <small className="float-start text-danger">{error}</small> : "",
                                error
                            }
                        }}
                    />
                </LocalizationProvider>
            </div>
        </div >
    );
}
